import { useEffect, useMemo } from "react";
import { Bell, Compass, Flame, MessageCircle, Pointer, Search, X } from "lucide-react";
import { APP_STORE_URL, PLAY_URL, storeHref, type WebPlatform } from "./appPromo";
import { APP_EDGES, APP_MISSING_TOOLS } from "./phone";
import { resolveImageUrl } from "./assets";
import type { InSnipCard } from "./types";
import "./PhoneDoor.css";

const GLYPH = { dive: Compass, search: Search, ask: MessageCircle, bell: Bell, flame: Flame } as const;

/**
 * THE APP, SHOWN RATHER THAN DESCRIBED.
 *
 * A phone drawn in CSS, holding today's REAL first two stories, with the top one swiping away
 * to reveal the one behind it. Four true things are visible at once that four sentences could
 * only assert: what a card looks like, that there is a deck behind it, that the deck is moved
 * with a thumb, and that the app has the whole screen with no browser bars in it.
 *
 * WHY THIS IS AN HONEST DEPICTION AND NOT A MOCKUP. The native app is this same React bundle
 * (see CLAUDE.md: one codebase, two surfaces), so the card layout drawn here is the layout the
 * app genuinely has, and the content is the actual round the visitor is being offered. The one
 * difference being illustrated, a screen with no browser chrome on it, is precisely the claim.
 * Nothing here is invented: masthead, photograph, category, headline, essence and a four-tab
 * bar are all things the app really shows. There is no fabricated screenshot in the repo and
 * this is deliberately not one.
 *
 * Falls back to a quiet empty frame rather than fake headlines when the feed has not loaded.
 */
export function AppShowcase({ cards, compact }: { cards: InSnipCard[]; compact?: boolean }) {
  const pair = useMemo(() => cards.filter((c) => c && c.headline).slice(0, 2), [cards]);
  if (!pair.length) return null;

  const slide = (card: InSnipCard, i: number) => (
    <div className={`ash-card ash-card-${i}`} key={card.id}>
      <div className="ash-pic">
        {card.imageUrl ? <img src={resolveImageUrl(card.imageUrl)} alt="" loading="lazy" decoding="async" /> : null}
      </div>
      <div className="ash-copy">
        <span className="ash-cat">{card.category || "Today"}</span>
        <strong className="ash-head">{card.headline}</strong>
        {card.essence ? <span className="ash-ess">{card.essence}</span> : null}
      </div>
    </div>
  );

  return (
    <div className={`ash${compact ? " ash-compact" : ""}`} aria-hidden="true">
      <div className="ash-phone">
        <span className="ash-notch" />
        <div className="ash-screen">
          <div className="ash-mast">
            In<i>Snip</i>
          </div>
          <div className="ash-deck">
            {pair[1] ? slide(pair[1], 1) : null}
            {slide(pair[0], 0)}
          </div>
          <div className="ash-nav">
            <i /><i /><i /><i />
          </div>
        </div>
      </div>
      <span className="ash-swipe">
        <Pointer size={13} aria-hidden="true" />
      </span>
    </div>
  );
}

/**
 * THE PHONE DOOR: the one time a phone-browser visitor is asked to take the app.
 *
 * It replaces AppWall, and the difference between them is the whole point. The wall was a
 * dead end: it took the entire viewport, showed not one story, and offered no way past. A
 * phone arriving from a shared link therefore got a sales pitch instead of the thing that was
 * shared with them, which is why it has been switched off since 2026-08-25.
 *
 * This is a door, not a wall, and it earns the ask three ways:
 *
 *  1. THE STORIES ARE REAL AND THEY ARE ALREADY HERE. It renders today's actual first three
 *     headlines with their publications. The reader is mounted and loaded BEHIND it, so
 *     declining does not fetch, redirect or reload anything: the glass lifts and the story is
 *     already there. Nobody is being asked to buy a pig in a poke.
 *  2. THE DECLINE IS REAL. "Read in the browser" is a plain, unpunished second button, not a
 *     grey link hidden in a corner. A phone visitor who wants to read now can read now.
 *  3. IT IS ASKED ONCE. The answer is stored (see phone.ts appChoice) and a decline is
 *     honoured for good. What remains afterwards is the quiet strip in the reader, which
 *     states what the app does differently and never takes the screen again.
 *
 * Every claim in APP_EDGES has to be true of the native build specifically, because this is an
 * argument a stranger can check with one install.
 */
export default function PhoneDoor({
  platform,
  cards,
  onTake,
  onDecline,
  onSeen,
}: {
  platform: WebPlatform;
  cards: InSnipCard[];
  onTake: (surface: string) => void;
  onDecline: () => void;
  onSeen: () => void;
}) {
  useEffect(() => {
    onSeen();
    // The door owns the viewport while it is up; the reader behind it must not scroll under it.
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape declines, because a modal that traps the keyboard is a wall wearing a door's clothes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDecline();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDecline]);

  // Three is the number that reads as "there is a round here" without becoming a feed. Only
  // cards that can actually be shown: a headline with no publication is not proof of anything.
  const proof = useMemo(
    () => cards.filter((c) => c && c.headline && c.primarySourceName).slice(0, 3),
    [cards],
  );

  const primaryHref = storeHref(platform);
  const primaryLabel =
    platform === "ios" ? "Get InSnip free on the App Store" : "Get InSnip free on Google Play";

  const dateLine = new Date().toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="pdoor" role="dialog" aria-modal="true" aria-labelledby="pdoor-h">
      <div className="pdoor-glow" aria-hidden="true" />
      <div className="pdoor-sheet">
        <header className="pdoor-mast">
          <span className="pdoor-word">
            In<i>Snip</i>
          </span>
          <span className="pdoor-date">{dateLine}</span>
        </header>

        <h1 id="pdoor-h" className="pdoor-h">
          Today's ten are ready.
          <span>The app is where they read best.</span>
        </h1>

        {/* The app itself, holding today's stories, before a word of the argument is read. */}
        <AppShowcase cards={cards} />

        {proof.length ? (
          <ol className="pdoor-proof" aria-label="Waiting in today's round">
            {proof.map((c, i) => (
              <li key={c.id}>
                <span className="pp-no" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="pp-text">
                  <strong>{c.headline}</strong>
                  <em>{c.primarySourceName}</em>
                </span>
              </li>
            ))}
          </ol>
        ) : null}

        <ul className="pdoor-edges" aria-label="What the app does differently">
          {APP_EDGES.map((edge) => {
            const Icon = GLYPH[edge.icon];
            return (
              <li key={edge.icon}>
                <Icon size={15} aria-hidden="true" />
                <span>{edge.line}</span>
              </li>
            );
          })}
        </ul>

        <div className="pdoor-acts">
          <a
            className="pdoor-cta"
            href={primaryHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onTake(platform === "ios" ? "door_ios_primary" : "door_android_primary")}
          >
            {primaryLabel}
          </a>
          <button type="button" className="pdoor-skip" onClick={onDecline}>
            Read in the browser
          </button>
        </div>

        <p className="pdoor-fine">Free, no ads in your feed, and about a minute to install.</p>

        {/* The other store stays reachable. Someone on an Android phone reading this may well
            be choosing a phone for someone else, and a dead end there costs us nothing to avoid. */}
        <div className="pdoor-badges" aria-label="Also available on">
          <a
            href={platform === "ios" ? PLAY_URL : APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onTake(platform === "ios" ? "door_android_alt" : "door_ios_alt")}
          >
            {platform === "ios" ? "Also on Google Play" : "Also on the App Store"}
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * THE ROUND PROMPT: the app, offered at the end of every round on a phone browser.
 *
 * This is the best moment the product has. Someone who has just finished ten stories has done
 * the thing the app exists for, and they did it on the worse of the two surfaces. They are not
 * being interrupted, they are being met at a natural stop: the round is over, and the question
 * is whether the next one happens here or somewhere better.
 *
 * It waits for the round summary rather than stacking on top of it (see App.tsx: it opens when
 * showInsight closes), so a reader never has two sheets over one screen.
 *
 * It shows the app rather than describing it, then says one true thing, chosen by the round
 * number so a reader who does three rounds is told three different things. Dismissal is a
 * plain button AND the scrim AND Escape, because this arrives on every round and anything
 * that arrives repeatedly has to be trivially easy to get past.
 */
export function RoundPrompt({
  round,
  storiesRead,
  cards,
  platform,
  onTake,
  onDismiss,
  onSeen,
}: {
  round: number;
  storiesRead: number;
  cards: InSnipCard[];
  platform: WebPlatform;
  onTake: (surface: string) => void;
  onDismiss: () => void;
  onSeen: () => void;
}) {
  useEffect(() => {
    onSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  // Round 1 gets the swipe, round 2 the alerts, and so on, so a second prompt is never a
  // repeat of the first. Rounds are 1-based.
  const edge = APP_EDGES[Math.max(0, round - 1) % APP_EDGES.length];
  const Icon = GLYPH[edge.icon];

  return (
    <div className="rprompt" role="dialog" aria-modal="true" aria-labelledby="rp-h">
      <button type="button" className="rp-scrim" aria-label="Not now" onClick={onDismiss} />
      <div className="rp-sheet">
        <button type="button" className="rp-x" onClick={onDismiss} aria-label="Not now">
          <X size={16} aria-hidden="true" />
        </button>

        <p className="rp-eyebrow">Round {round} done</p>
        <h2 id="rp-h" className="rp-h">
          {storiesRead > 0
            ? `That is ${storiesRead} ${storiesRead === 1 ? "story" : "stories"}, read properly.`
            : "That is the round, read properly."}
        </h2>

        <AppShowcase cards={cards} compact />

        <p className="rp-edge">
          <Icon size={15} aria-hidden="true" />
          <span>{edge.line}</span>
        </p>

        <a
          className="rp-cta"
          href={storeHref(platform)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onTake(`round${round}_${platform}`)}
        >
          {platform === "ios" ? "Get the free app" : "Get the free app"}
        </a>
        <button type="button" className="rp-skip" onClick={onDismiss}>
          Keep reading here
        </button>
      </div>
    </div>
  );
}

/**
 * THE STANDING INVITATION, shown to a phone reader who declined the door.
 *
 * "Show them what they are missing" has an obvious wrong answer, which is to keep asking. It
 * had a second wrong answer until 2026-09-08, which was to assert a quality (smooth, fast,
 * full screen) that the reader has no way to check from where they are standing.
 *
 * Now it names TOOLS, because the phone browser genuinely no longer has them. A reader who
 * wanted to go deeper on this story, or look something up, or ask a question, just failed to
 * find the thing they wanted, and this is the answer to the question they already asked. That
 * is why the three are stated outright rather than rotated one per card: rotation was right
 * when the lines were interchangeable virtues, and wrong now that they are a specific set of
 * missing doors. The reader should be able to see the whole trade in one glance and decide.
 *
 * It stays inline in the card, so it scrolls with the story instead of becoming a second fixed
 * bar on a screen that just lost one.
 */
export function AppNudge({
  platform,
  onClick,
}: {
  platform: WebPlatform;
  onClick: (surface: string) => void;
}) {
  return (
    <a
      className="app-nudge"
      href={storeHref(platform)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onClick("nudge_tools")}
      /* The visible lead-in is two words because this strip has to be ONE line: it sat two
         lines and 88px tall under a story that only had 223px of its own on a short phone.
         The full sentence survives as the link's accessible name, so a screen reader still
         hears what a sighted reader infers from the button sitting beside the tools. */
      aria-label={`${APP_MISSING_TOOLS.join(", ")} are in the InSnip app. Get the app.`}
    >
      <span className="app-nudge-copy">
        <strong>In the app</strong>
        <span className="app-nudge-tools" aria-hidden="true">
          {APP_MISSING_TOOLS.map((tool) => {
            const Icon = tool === "Search" ? Search : tool === "Ask" ? MessageCircle : Compass;
            return (
              <span className="app-nudge-tool" key={tool}>
                <Icon size={12} aria-hidden="true" />
                {tool}
              </span>
            );
          })}
        </span>
      </span>
      <span className="app-nudge-go" aria-hidden="true">
        Get the app
      </span>
    </a>
  );
}
